import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { SchoolCalendarSection } from "pages/app/workspace/sections/school-calendar-section";
import { SchoolMatchesSection } from "pages/app/workspace/sections/school-matches-section";
import { SchoolScoresheetsSection } from "pages/app/workspace/sections/school-scoresheets-section";
import {
  SchoolHubContentStack,
  SchoolHubTabButton,
  SchoolHubTabRow,
} from "pages/app/workspace/styles/school-hub.page.inline.styles";
import { getVisiblePortalSections } from "utils/portal-access";

export type ScheduleView = "calendar" | "list";
export type ScheduleListFocus = "fixtures" | "scoring";

const parseScheduleView = (
  search: string,
  visibleSections: string[],
): { focus: ScheduleListFocus; view: ScheduleView } => {
  const params = new URLSearchParams(search);
  const requestedView = params.get("view") as ScheduleView | null;
  const requestedFocus = params.get("focus") as ScheduleListFocus | null;

  const canCalendar =
    visibleSections.includes("school_calendar") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");

  const canFixtures =
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");

  const canScoring =
    visibleSections.includes("school_scoresheets") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_dashboard");

  let view: ScheduleView = "calendar";
  if (requestedView === "list" && canFixtures) {
    view = "list";
  } else if (requestedView === "calendar" && canCalendar) {
    view = "calendar";
  } else if (!canCalendar && canFixtures) {
    view = "list";
  }

  let focus: ScheduleListFocus = "fixtures";
  if (view === "list" && requestedFocus === "scoring" && canScoring) {
    focus = "scoring";
  }

  return { focus, view };
};

const SchedulePage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { portalAccess } = useAuth();
  const { data: currentUser, isLoading: isProfileLoading } = useQuery(buildCurrentUserQueryOptions());
  const effectivePortalAccess = currentUser?.portal_access || portalAccess;
  const visibleSections = getVisiblePortalSections(effectivePortalAccess);

  const { focus, view } = useMemo(
    () => parseScheduleView(location.search, visibleSections),
    [location.search, visibleSections],
  );

  const canCalendar =
    visibleSections.includes("school_calendar") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");

  const canFixtures =
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");

  const canScoring =
    visibleSections.includes("school_scoresheets") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_dashboard");

  const setView = useCallback(
    (nextView: ScheduleView, nextFocus: ScheduleListFocus = "fixtures") => {
      const params = new URLSearchParams();
      if (nextView !== "calendar") {
        params.set("view", nextView);
      }
      if (nextView === "list" && nextFocus === "scoring") {
        params.set("focus", "scoring");
      }
      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      });
    },
    [history, location.pathname],
  );

  const listBackTo = `/workspace/schedule${location.search || ""}`;

  const headerTabs = useMemo(
    () => (
      <SchoolHubTabRow>
        {canCalendar ? (
          <SchoolHubTabButton
            $active={view === "calendar"}
            onClick={() => setView("calendar")}
            type="button"
          >
            Calendar
          </SchoolHubTabButton>
        ) : null}
        {canFixtures ? (
          <SchoolHubTabButton
            $active={view === "list" && focus === "fixtures"}
            onClick={() => setView("list", "fixtures")}
            type="button"
          >
            Fixtures
          </SchoolHubTabButton>
        ) : null}
        {canScoring ? (
          <SchoolHubTabButton
            $active={view === "list" && focus === "scoring"}
            onClick={() => setView("list", "scoring")}
            type="button"
          >
            Scoring
          </SchoolHubTabButton>
        ) : null}
      </SchoolHubTabRow>
    ),
    [canCalendar, canFixtures, canScoring, focus, setView, view],
  );

  return (
    <AppLayout route="/workspace/schedule">
      <SchoolOpsHeader tabs={headerTabs} />

      <SchoolHubContentStack>
        {!isProfileLoading && view === "calendar" ? (
          <SchoolCalendarSection embedded portalAccess={effectivePortalAccess} />
        ) : null}

        {!isProfileLoading && view === "list" && focus === "fixtures" ? (
          <SchoolMatchesSection
            embedded
            listBackTo={listBackTo}
            portalAccess={effectivePortalAccess}
          />
        ) : null}

        {!isProfileLoading && view === "list" && focus === "scoring" ? (
          <SchoolScoresheetsSection
            embedded
            listBackTo={listBackTo}
            portalAccess={effectivePortalAccess}
          />
        ) : null}
      </SchoolHubContentStack>
    </AppLayout>
  );
};

export default SchedulePage;
